import React, { useState } from 'react';
import { X, Loader2, MapPin } from 'lucide-react';
import LocationSelectionModal from './LocationSelectionModal';

const CreateProjectModal = ({
  isOpen,
  onClose,
  onSave,
  isLoading = false
}) => {
  const [formData, setFormData] = useState({
    projectName: '',
    location: null, // Will store coordinates and address
    startDate: '',
    endDate: ''
  });
  const [errors, setErrors] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [locationModalOpen, setLocationModalOpen] = useState(false);

  const validateForm = () => {
    const newErrors = {};

    // Validate required fields
    if (!formData.projectName.trim()) {
      newErrors.projectName = 'Projektname ist erforderlich';
    } else if (formData.projectName.trim().length < 2) {
      newErrors.projectName = 'Projektname muss mindestens 2 Zeichen lang sein';
    } else if (!/^[a-zA-Z0-9\s\-_äöüÄÖÜß]+$/.test(formData.projectName.trim())) {
      newErrors.projectName = 'Projektname enthält ungültige Zeichen';
    }

    if (!formData.location || !formData.location.lat || !formData.location.lng) {
      newErrors.location = 'Standort ist erforderlich';
    }

    // Validate dates if provided
    if (formData.startDate && formData.endDate) {
      const startDate = new Date(formData.startDate);
      const endDate = new Date(formData.endDate);

      if (endDate <= startDate) {
        newErrors.endDate = 'Enddatum muss nach dem Startdatum liegen';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error for this field when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    setIsSaving(true);
    try {
      await onSave(formData);
      // Reset form after successful save
      setFormData({
        projectName: '',
        location: null,
        startDate: '',
        endDate: ''
      });
      setErrors({});
    } catch (error) {
      console.error('Error saving project:', error);
      // Error handling is done by parent component
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    if (!isSaving) {
      // Reset form when canceling
      setFormData({
        projectName: '',
        location: null,
        startDate: '',
        endDate: ''
      });
      setErrors({});
      setLocationModalOpen(false);
      onClose();
    }
  };

  const handleLocationSelect = (location) => {
    setFormData(prev => ({
      ...prev,
      location: location
    }));

    // Clear location error when location is selected
    if (errors.location) {
      setErrors(prev => ({
        ...prev,
        location: ''
      }));
    }
  };

  const handleOpenLocationModal = () => {
    setLocationModalOpen(true);
  };

  const handleCloseLocationModal = () => {
    setLocationModalOpen(false);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg p-8 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold">Neues Projekt erstellen</h2>
          <button
            onClick={handleCancel}
            className="text-gray-500 hover:text-gray-700 transition-colors"
            disabled={isSaving}
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-4 mb-6">
          {/* Project Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Projektname <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.projectName}
              onChange={(e) => handleInputChange('projectName', e.target.value)}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 ${
                errors.projectName ? 'border-red-500 bg-red-50' : 'border-gray-300'
              }`}
              placeholder="z.B. A7 Kassel-Hannover"
              disabled={isSaving}
            />
            {errors.projectName && (
              <p className="text-red-500 text-sm mt-1">{errors.projectName}</p>
            )}
          </div>

          {/* Location */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Standort <span className="text-red-500">*</span>
            </label>
            <div
              onClick={handleOpenLocationModal}
              className={`w-full px-3 py-2 border rounded-md cursor-pointer transition-colors hover:bg-gray-50 flex items-center gap-2 ${
                errors.location ? 'border-red-500 bg-red-50' : 'border-gray-300'
              } ${isSaving ? 'cursor-not-allowed opacity-50' : ''}`}
            >
              <MapPin className="h-4 w-4 text-gray-400" />
              <span className={formData.location ? 'text-gray-900' : 'text-gray-500'}>
                {formData.location ? formData.location.address : 'Standort auf Karte auswählen...'}
              </span>
            </div>
            {errors.location && (
              <p className="text-red-500 text-sm mt-1">{errors.location}</p>
            )}
          </div>

          {/* Date Range */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Projektzeitraum Start
              </label>
              <input
                type="date"
                value={formData.startDate}
                onChange={(e) => handleInputChange('startDate', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                disabled={isSaving}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Projektzeitraum Ende
              </label>
              <input
                type="date"
                value={formData.endDate}
                onChange={(e) => handleInputChange('endDate', e.target.value)}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 ${
                  errors.endDate ? 'border-red-500 bg-red-50' : 'border-gray-300'
                }`}
                disabled={isSaving}
              />
              {errors.endDate && (
                <p className="text-red-500 text-sm mt-1">{errors.endDate}</p>
              )}
            </div>
          </div>
        </div>

        {isSaving ? (
          <div className="flex flex-col items-center justify-center py-4 mb-6">
            <Loader2 className="h-8 w-8 animate-spin text-green-600 mb-2" />
            <p className="text-gray-700">Projekt wird erstellt...</p>
          </div>
        ) : (
          <div className="flex justify-end gap-4">
            <button
              onClick={handleCancel}
              className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
              disabled={isSaving}
            >
              Abbrechen
            </button>
            <button
              onClick={handleSave}
              className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
              disabled={isSaving}
            >
              Projekt erstellen
            </button>
          </div>
        )}

        {/* Location Selection Modal */}
        <LocationSelectionModal
          isOpen={locationModalOpen}
          onClose={handleCloseLocationModal}
          onSave={handleLocationSelect}
          initialLocation={formData.location}
        />
      </div>
    </div>
  );
};

export default CreateProjectModal;
